import React, { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import { AlertTriangle, Plus, Home, Building2 } from 'lucide-react';
import { CalendarShiftCard } from './CalendarShiftCard';
import { shiftDurationHours } from './calendarUtils';

// Deputy-style staff × days grid (Week view).
// Behaviour identical to the previous inline implementation.
export function CalendarWeekView({ days, filteredShifts, staff, participants, groupBy, onShiftClick, onCellClick, onDragDrop, onRowClick, permissions }) {
  const canDrag = permissions.includes('roster.update') || permissions.includes('roster.manage');
  const isParticipantView = groupBy === 'participant';
  const isSilView = groupBy === 'sil';
  const isCentreView = groupBy === 'centre';

  // Group shifts per-row per-day based on groupBy
  const rowShiftsMap = useMemo(() => {
    const map = {};
    filteredShifts.forEach(shift => {
      if (isParticipantView) {
        const pid = shift.participant_id || 'unassigned';
        if (!map[pid]) map[pid] = {};
        if (!map[pid][shift.date]) map[pid][shift.date] = [];
        map[pid][shift.date].push(shift);
      } else if (isSilView) {
        const hid = shift.house_id || shift._sil_house_id || 'no_house';
        if (!map[hid]) map[hid] = {};
        if (!map[hid][shift.date]) map[hid][shift.date] = [];
        map[hid][shift.date].push(shift);
      } else if (isCentreView) {
        const cid = shift.session_id || 'no_session';
        if (!map[cid]) map[cid] = {};
        if (!map[cid][shift.date]) map[cid][shift.date] = [];
        map[cid][shift.date].push(shift);
      } else {
        (shift.assigned_staff || []).forEach(s => {
          if (!map[s.id]) map[s.id] = {};
          if (!map[s.id][shift.date]) map[s.id][shift.date] = [];
          map[s.id][shift.date].push(shift);
        });
      }
    });
    return map;
  }, [filteredShifts, isParticipantView, isSilView, isCentreView]);

  // Build rows based on groupBy
  const rows = useMemo(() => {
    if (isParticipantView) {
      const pidsWithShifts = new Set(Object.keys(rowShiftsMap));
      const result = (participants || []).filter(p => pidsWithShifts.has(p.id)).map(p => ({
        id: p.id, label: `${p.first_name} ${p.last_name}`, initials: (p.first_name || '?')[0].toUpperCase(), kind: 'participant',
      }));
      if (rowShiftsMap['unassigned']) result.push({ id: 'unassigned', label: 'Unassigned', initials: '?', kind: 'participant' });
      return result;
    }
    if (isSilView) {
      const r = [];
      Object.keys(rowShiftsMap).forEach(hid => {
        if (hid === 'no_house') {
          const allShifts = Object.values(rowShiftsMap[hid]).flat();
          r.push({ id: 'no_house', label: 'No SIL House', initials: '—', shiftCount: allShifts.length, kind: 'sil' });
        } else {
          const allShifts = Object.values(rowShiftsMap[hid]).flat();
          const houseName = allShifts[0]?.house_name || allShifts[0]?._sil_house_name || `House ${hid.slice(-4)}`;
          r.push({ id: hid, label: houseName, initials: houseName[0]?.toUpperCase() || 'H', shiftCount: allShifts.length, kind: 'sil' });
        }
      });
      return r;
    }
    if (isCentreView) {
      const r = [];
      Object.keys(rowShiftsMap).forEach(cid => {
        if (cid === 'no_session') {
          const allShifts = Object.values(rowShiftsMap[cid]).flat();
          r.push({ id: 'no_session', label: 'No Centre Session', initials: '—', shiftCount: allShifts.length, kind: 'centre' });
        } else {
          const allShifts = Object.values(rowShiftsMap[cid]).flat();
          const sessName = allShifts[0]?.session_name || `Session ${cid.slice(-4)}`;
          r.push({ id: cid, label: sessName, initials: (sessName[0] || 'C').toUpperCase(), shiftCount: allShifts.length, kind: 'centre' });
        }
      });
      return r;
    }
    return staff.map(s => ({ id: s.user_id || s.id, label: s.full_name, initials: (s.full_name || '?')[0].toUpperCase(), kind: 'staff' }));
  }, [isParticipantView, isSilView, isCentreView, participants, staff, rowShiftsMap]);

  const openShiftsMap = useMemo(() => {
    const map = {};
    filteredShifts.filter(s => !s.assigned_staff?.length).forEach(shift => {
      if (!map[shift.date]) map[shift.date] = [];
      map[shift.date].push(shift);
    });
    return map;
  }, [filteredShifts]);

  const totalOpen = days.reduce((a, d) => a + (openShiftsMap[format(d, 'yyyy-MM-dd')] || []).length, 0);

  const handleDragStart = (e, shift) => {
    e.dataTransfer.setData('shiftId', shift.id);
    e.dataTransfer.setData('shiftDate', shift.date);
  };

  const handleDrop = (e, newDate) => {
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('shiftId');
    const oldDate = e.dataTransfer.getData('shiftDate');
    if (shiftId && oldDate !== newDate) onDragDrop(shiftId, newDate);
  };

  const handleDragOver = (e) => e.preventDefault();

  return (
    <div>
      {/* Header */}
      <div className="flex sticky top-0 z-20 bg-white border-b">
        <div className="w-48 flex-shrink-0 p-2 border-r" />
        {days.map(day => (
          <div key={day.toISOString()} className={`flex-1 min-w-[120px] text-center px-1 py-1.5 border-r ${isToday(day) ? 'bg-blue-50' : ''}`}>
            <p className={`text-[10px] uppercase tracking-wider ${isToday(day) ? 'text-blue-600 font-bold' : 'text-muted-foreground'}`}>{format(day, 'EEE')}</p>
            <p className={`text-sm font-semibold ${isToday(day) ? 'text-blue-700' : 'text-slate-800'}`}>{format(day, 'd MMM')}</p>
          </div>
        ))}
      </div>

      {/* Open Shifts */}
      {totalOpen > 0 && (
        <div className="flex border-b bg-red-50/30">
          <div className="w-48 flex-shrink-0 p-2 border-r bg-red-50 sticky left-0 z-10">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /></div>
              <div><p className="text-xs font-medium text-red-700">Open Shifts</p><p className="text-[10px] text-red-500">{totalOpen} unfilled</p></div>
            </div>
          </div>
          {days.map(day => {
            const dk = format(day, 'yyyy-MM-dd');
            return (
              <div key={dk} className={`flex-1 min-w-[120px] p-1 border-r ${isToday(day) ? 'bg-blue-50/30' : ''}`}
                onDrop={e => handleDrop(e, dk)} onDragOver={handleDragOver}>
                {(openShiftsMap[dk] || []).map(s => <CalendarShiftCard key={s.id} shift={s} onClick={onShiftClick} compact={(openShiftsMap[dk]||[]).length > 2} draggable={canDrag} onDragStart={handleDragStart} />)}
              </div>
            );
          })}
        </div>
      )}

      {/* Rows */}
      {rows.map(row => {
        const sMap = rowShiftsMap[row.id] || {};
        const weekH = days.reduce((a, d) => {
          return a + (sMap[format(d, 'yyyy-MM-dd')] || []).reduce((h, s) => h + shiftDurationHours(s), 0);
        }, 0);
        return (
          <div key={row.id} className="flex border-b hover:bg-slate-50/50 transition-colors" data-testid={`row-${row.id}`}>
            <div
              className={`w-48 flex-shrink-0 p-2 border-r bg-white sticky left-0 z-10 ${(row.kind === 'sil' || row.kind === 'centre') && row.id !== 'no_house' && row.id !== 'no_session' ? 'cursor-pointer hover:bg-slate-100' : ''}`}
              onClick={() => {
                if ((row.kind === 'sil' || row.kind === 'centre') && row.id !== 'no_house' && row.id !== 'no_session' && onRowClick) {
                  onRowClick(row);
                }
              }}
              data-testid={`row-label-${row.id}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${row.kind === 'sil' ? 'bg-emerald-100 text-emerald-700' : row.kind === 'centre' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                  {row.kind === 'sil' ? <Home className="w-3.5 h-3.5" /> : row.kind === 'centre' ? <Building2 className="w-3.5 h-3.5" /> : row.initials}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{row.label}</p>
                  <p className="text-[10px] text-muted-foreground">{weekH.toFixed(1)}h{(row.kind === 'sil' || row.kind === 'centre') && row.id !== 'no_house' && row.id !== 'no_session' ? ' · view' : ''}</p>
                </div>
              </div>
            </div>
            {days.map(day => {
              const dk = format(day, 'yyyy-MM-dd');
              const dayShifts = sMap[dk] || [];
              const cellStaff = row.kind === 'staff' ? { id: row.id } : null;
              return (
                <div key={dk} className={`flex-1 min-w-[120px] p-1 border-r ${isToday(day) ? 'bg-blue-50/40' : ''}`}
                  onDrop={e => handleDrop(e, dk)} onDragOver={handleDragOver}>
                  {dayShifts.map(s => <CalendarShiftCard key={s.id} shift={s} onClick={onShiftClick} compact={dayShifts.length > 2} draggable={canDrag} onDragStart={handleDragStart} />)}
                  {dayShifts.length === 0 ? (
                    <div className="h-full min-h-[40px] flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer" onClick={() => onCellClick(cellStaff, dk)}><Plus className="w-3.5 h-3.5 text-slate-300" /></div>
                  ) : (
                    <div className="flex items-center justify-center py-1 cursor-pointer opacity-0 hover:opacity-100 transition-opacity border border-dashed border-slate-300 rounded mt-1" onClick={() => onCellClick(cellStaff, dk)}><Plus className="w-3 h-3 text-slate-400" /></div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Daily Totals */}
      <div className="flex border-t-2 bg-slate-50 sticky bottom-0">
        <div className="w-48 flex-shrink-0 p-2 border-r font-medium text-xs text-slate-600 sticky left-0 bg-slate-50 z-10">Daily Totals</div>
        {days.map(day => {
          const dk = format(day, 'yyyy-MM-dd');
          const ds = filteredShifts.filter(s => s.date === dk);
          const th = ds.reduce((h, s) => h + shiftDurationHours(s), 0);
          return (
            <div key={dk} className={`flex-1 min-w-[120px] p-2 border-r text-center ${isToday(day) ? 'bg-blue-50/50' : ''}`}>
              <p className="text-xs font-bold text-slate-700">{ds.length} shifts</p>
              <p className="text-[10px] text-muted-foreground">{th.toFixed(1)}h</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
